import { useState, useEffect } from 'react';
import { adminAPI } from '../../utils/api';

import { useToast, apiError } from '../../components/Toast';
import { Check, Save } from 'lucide-react';


const SECTION_ICONS = {
  general:    '⚙️',
  security:   '🔒',
  email:      '✉️',
  notifications: '🔔',
  system:     '🖥️',
};

const FIELD_TYPES = {
  'boolean':  'toggle',
  'number':   'number',
  'email':    'email',
  'url':      'url',
};

function SettingField({ setting, value, onChange }) {
  const type = setting.type || 'text';
  if (type === 'boolean' || value === 'true' || value === 'false') {
    const isOn = value === 'true' || value === true;
    return (
      <button onClick={() => onChange(isOn ? 'false' : 'true')}
        className="relative w-11 h-6 rounded-full transition-colors"
        style={{ background: isOn ? '#2E539F' : 'var(--border)' }}>
        <span className="absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all"
          style={{ left: isOn ? 22 : 4 }}/>
      </button>
    );
  }
  if (type === 'textarea') {
    return <textarea className="input text-sm" rows={3} value={value||''} onChange={e=>onChange(e.target.value)}/>;
  }
  return (
    <input type={FIELD_TYPES[type]||'text'} className="input text-sm max-w-xs"
      value={value||''} onChange={e=>onChange(e.target.value)}/>
  );
}

export default function AdminSettingsPage() {
  const toast = useToast();
  const [settings, setSettings] = useState([]);
  const [error, setError] = useState('');
  const [edits,    setEdits]    = useState({});
  const [saving,   setSaving]   = useState({});
  const [saved,    setSaved]    = useState({});
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    adminAPI.getSettings()
      .then(r => setSettings(r.data || []))
      .catch(e => setError(e.response?.data?.message || 'Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const getValue = s => edits[s.key] !== undefined ? edits[s.key] : s.value;
  const isDirty  = s => edits[s.key] !== undefined && edits[s.key] !== s.value;

  const handleChange = (key, value) => setEdits(e => ({ ...e, [key]: value }));

  const handleSave = async (key) => {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      const val = edits[key] !== undefined ? edits[key]
        : settings.find(s => s.key === key)?.value;
      await adminAPI.updateSetting(key, val);
      setSaved(s => ({ ...s, [key]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [key]: false })), 2500);
      const r = await adminAPI.getSettings(); setSettings(r.data||[]);
      setEdits(e => { const n={...e}; delete n[key]; return n; });
    } catch(e) { toast.error(apiError(e, 'Failed to save setting.')); }
    finally { setSaving(s => ({ ...s, [key]: false })); }
  };

  const handleSaveAll = async () => {
    const dirtyKeys = Object.keys(edits);
    for (const key of dirtyKeys) await handleSave(key);
  };

  const categories = [...new Set(settings.map(s => s.category || 'general'))];
  const dirtyCount = Object.keys(edits).length;

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>;
  if (error)   return <div className="card py-12 text-center"><p className="text-red-500">⚠ {error}</p><button className="btn-secondary mt-4 text-sm" onClick={()=>{ setError(''); setLoading(true); adminAPI.getSettings().then(r=>setSettings(r.data||[])).catch(e=>setError(e.response?.data?.message||'Failed')).finally(()=>setLoading(false)); }}>Retry</button></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">System Settings</h1>
          <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>
            Configure application behaviour and system parameters
          </p>
        </div>
        {dirtyCount > 0 && (
          <button onClick={handleSaveAll} className="btn-primary flex items-center gap-2 text-sm">
            <Save size={14}/> Save {dirtyCount} change{dirtyCount!==1?'s':''}
          </button>
        )}
      </div>

      {settings.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-3xl mb-3">⚙️</p>
          <p className="font-semibold" style={{ color:'var(--text)' }}>No settings configured</p>
          <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>
            System settings will appear here once the settings table is seeded
          </p>
        </div>
      ) : (
        categories.map(cat => {
          const catSettings = settings.filter(s => (s.category||'general') === cat);
          return (
            <div key={cat} className="card overflow-hidden p-0">
              <div className="px-5 py-4 border-b flex items-center gap-3"
                style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                <span className="text-lg">{SECTION_ICONS[cat] || '⚙️'}</span>
                <div>
                  <h3 className="font-semibold text-sm capitalize" style={{ color:'var(--text)' }}>{cat}</h3>
                  <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{catSettings.length} setting{catSettings.length!==1?'s':''}</p>
                </div>
              </div>
              <div className="divide-y" style={{ borderColor:'var(--border)' }}>
                {catSettings.map(s => (
                  <div key={s.key} className="px-5 py-4 flex items-start gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-semibold" style={{ color:'var(--text)' }}>
                          {s.label || s.key.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}
                        </p>
                        {isDirty(s) && (
                          <span className="text-xs px-1.5 py-0.5 rounded font-medium"
                            style={{ background:'rgba(245,158,11,.15)', color:'#d97706' }}>
                            Unsaved
                          </span>
                        )}
                        {saved[s.key] && (
                          <span className="text-xs px-1.5 py-0.5 rounded font-medium flex items-center gap-1"
                            style={{ background:'rgba(16,185,129,.15)', color:'#059669' }}>
                            <Check size={14}/> Saved
                          </span>
                        )}
                      </div>
                      {s.description && (
                        <p className="text-xs mb-2" style={{ color:'var(--text-secondary)' }}>{s.description}</p>
                      )}
                      <SettingField setting={s} value={getValue(s)} onChange={v => handleChange(s.key, v)}/>
                    </div>
                    <div className="flex-shrink-0 pt-1">
                      <button onClick={() => handleSave(s.key)}
                        disabled={!isDirty(s) || saving[s.key]}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors disabled:opacity-40"
                        style={{ borderColor:'var(--border)', color:'var(--text-secondary)', background:'var(--surface-alt)' }}>
                        {saving[s.key]
                          ? <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"/>
                          : <Save size={14}/>}
                        Save
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
